import { useCallback, useEffect, useRef, useState } from 'react';
import type { UA } from 'jssip';
import type { EndEvent, RTCSession } from 'jssip/lib/RTCSession';

/**
 * De typen komen uit jssip zelf; alleen `import type`, dus er komt niets extra in
 * de bundel -- de bibliotheek zelf blijft achter de dynamische import staan zodat
 * hij niet in de eerste render meegeladen wordt.
 *
 * Waarom dat de moeite is: `ua.on` en `session.on` zijn in jssip getypeerd op een
 * eventmap, dus zodra de handlers hun `any`-annotatie kwijt zijn leidt TypeScript
 * het eventtype zelf af. Die annotaties stonden er niet omdat het type onbekend
 * was, maar omdat niemand had gekeken.
 */

export type SipCallState =
  | 'idle'
  | 'registering'
  | 'registered'
  | 'register-failed'
  | 'ringing-out'
  | 'ringing-in'
  | 'in-call'
  | 'ended';

export type SipConfig = {
  ws_uri: string;
  sip_uri: string;
  sip_password: string;
  display_name?: string | null;
  stun_server?: string | null;
  dial_prefix?: string | null;
};

export type SipActiveCall = {
  direction: 'inbound' | 'outbound';
  peer: string;
  startedAt: number;
  session: RTCSession;
  remoteStream: MediaStream | null;
  localStream: MediaStream | null;
};

/**
 * Verbindingen waar de audio al aan hangt.
 *
 * Buiten de hook, want het gaat om de verbinding en niet om een render. WeakSet en
 * geen Set: een afgelopen gesprek mag zijn RTCPeerConnection kwijtraken.
 */
const gebondenVerbindingen = new WeakSet<RTCPeerConnection>();

/**
 * WebRTC SIP softphone via JsSIP (WSS to FreePBX/Asterisk).
 * Replaces the previous Azure Communication Services flow.
 */
export function useSip(cfg: SipConfig | null, activationKey = 0) {
  const uaRef = useRef<UA | null>(null);
  const [state, setState] = useState<SipCallState>('idle');
  const [call, setCall] = useState<SipActiveCall | null>(null);
  const [error, setError] = useState<string | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const registrationTimeoutRef = useRef<number | null>(null);
  const wsConnectedRef = useRef<boolean>(false);

  const ensureRemoteAudioElement = useCallback(() => {
    if (typeof document === 'undefined') return null;
    if (remoteAudioRef.current?.isConnected) return remoteAudioRef.current;
    const el = document.createElement('audio');
    el.autoplay = true;
    el.muted = false;
    el.volume = 1;
    el.setAttribute('playsinline', 'true');
    el.setAttribute('data-sip-remote-audio', 'true');
    document.body.appendChild(el);
    remoteAudioRef.current = el;
    return el;
  }, []);

  const unlockRemoteAudio = useCallback(() => {
    const el = ensureRemoteAudioElement();
    if (!el) return;
    try {
      // Safari kent AudioContext alleen onder de oude naam. Een smalle beschrijving
      // van dat ene veld, in plaats van het hele window als any te behandelen.
      const Context =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Context) throw new Error('Geen AudioContext beschikbaar');
      const ac = new Context();
      const dst = ac.createMediaStreamDestination();
      const osc = ac.createOscillator();
      const gain = ac.createGain();
      gain.gain.value = 0;
      osc.connect(gain).connect(dst);
      osc.start();
      el.srcObject = dst.stream;
      el.play().catch(() => {});
      setTimeout(() => { try { osc.stop(); ac.close(); } catch { /* ignore */ } }, 250);
    } catch (err) {
      console.warn('[SIP] unlock remote audio failed:', err);
    }
  }, [ensureRemoteAudioElement]);

  const playRemoteStream = useCallback((stream: MediaStream) => {
    const el = ensureRemoteAudioElement();
    if (!el) return;
    el.muted = false;
    el.volume = 1;
    if (el.srcObject !== stream) el.srcObject = stream;
    el.play().catch((err) => console.warn('[SIP] remote audio play() failed:', err));
  }, [ensureRemoteAudioElement]);

  const bindRemoteAudioTrack = useCallback((track: MediaStreamTrack, stream?: MediaStream) => {
    if (track.kind !== 'audio') return;
    const remoteStream = stream ?? new MediaStream([track]);
    playRemoteStream(remoteStream);
    setCall((c) => (c ? { ...c, remoteStream } : c));
  }, [playRemoteStream]);

  const attachPeerConnectionAudio = useCallback((pc?: RTCPeerConnection | null) => {
    // Eén keer binden per verbinding. Dat stond eerst als een eigen veld op het
    // object gestempeld; een WeakSet doet hetzelfde zonder de RTCPeerConnection van
    // een eigenschap te voorzien die daar niet hoort, en houdt hem niet in leven.
    if (!pc || gebondenVerbindingen.has(pc)) return;
    gebondenVerbindingen.add(pc);
    pc.addEventListener('track', (ev) => {
      bindRemoteAudioTrack(ev.track, ev.streams[0] ?? undefined);
    });
    pc.getReceivers?.().forEach((receiver) => {
      if (receiver.track?.kind === 'audio') bindRemoteAudioTrack(receiver.track);
    });
  }, [bindRemoteAudioTrack]);

  useEffect(() => {
    return () => {
      const el = remoteAudioRef.current;
      if (el) { el.srcObject = null; el.remove(); }
      remoteAudioRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!cfg || activationKey === 0) {
      setState('idle');
      setError(null);
      setCall(null);
      return;
    }
    let cancelled = false;

    const clearRegistrationTimeout = () => {
      if (registrationTimeoutRef.current !== null) {
        window.clearTimeout(registrationTimeoutRef.current);
        registrationTimeoutRef.current = null;
      }
    };

    (async () => {
      try {
        setState('registering');
        setError(null);
        const JsSIP = (await import('jssip')).default;
        const socket = new JsSIP.WebSocketInterface(cfg.ws_uri);
        const ua = new JsSIP.UA({
          sockets: [socket],
          uri: cfg.sip_uri,
          password: cfg.sip_password,
          display_name: cfg.display_name || undefined,
          register: true,
          session_timers: false,
        });

        ua.on('connected', () => {
          if (cancelled) return;
          // De WebSocket (WSS) is open; wacht nog op echte SIP REGISTER-response.
          wsConnectedRef.current = true;
          console.info('[SIP] websocket connected:', cfg.ws_uri);
        });
        ua.on('registered', () => {
          if (cancelled) return;
          clearRegistrationTimeout();
          setError(null);
          setState('registered');
        });
        ua.on('unregistered', () => {
          if (cancelled) return;
          clearRegistrationTimeout();
          setState('idle');
        });
        ua.on('registrationFailed', (e) => {
          if (cancelled) return;
          clearRegistrationTimeout();
          setState('register-failed');
          console.error('[SIP] registrationFailed', e);
          const status = e?.response?.status_code;
          const reason = e?.response?.reason_phrase;
          setError(
            `SIP-registratie mislukt: ${e?.cause ?? 'onbekend'}${status ? ` (${status} ${reason ?? ''})` : ''} — controleer extensie, wachtwoord en transport (WSS) op de PBX.`
          );
        });
        ua.on('disconnected', (e) => {
          if (cancelled) return;
          console.warn('[SIP] websocket disconnected', {
            code: e?.code,
            reason: e?.reason,
            error: e?.error,
            wsConnectedBefore: wsConnectedRef.current,
          });
          if (uaRef.current?.isRegistered?.()) {
            setState('registered');
          }
        });
        ua.on('newRTCSession', (data) => {
          const session = data.session;
          const direction = session.direction === 'incoming' ? 'inbound' : 'outbound';
          const peer = session.remote_identity?.uri?.user ?? 'onbekend';

          const onAccepted = () => setState('in-call');
          const onEnded = () => {
            setCall(null);
            if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
            const u = uaRef.current;
            setState(u?.isRegistered?.() ? 'registered' : 'ended');
          };
          const onFailed = (e: EndEvent) => {
            // EndEvent.message is een request of een response; status_code en
            // reason_phrase staan alleen op die tweede. Vandaar deze smalle blik in
            // plaats van het hele event als any.
            const bericht = e?.message as
              | { status_code?: number; reason_phrase?: string }
              | undefined;
            const status = bericht?.status_code;
            const reason = bericht?.reason_phrase;
            const cause = e?.cause ?? 'unknown';
            const parts = [cause];
            if (status) parts.push(`${status}${reason ? ` ${reason}` : ''}`);
            console.error('[SIP] failed', { cause, status, reason, originator: e?.originator, event: e });
            setError(`Oproep mislukt: ${parts.join(' — ')}`);
            onEnded();
          };

          session.on('accepted', onAccepted);
          session.on('confirmed', onAccepted);
          session.on('ended', onEnded);
          session.on('failed', onFailed);
          session.on('peerconnection', (pcData) => {
            attachPeerConnectionAudio(pcData.peerconnection as RTCPeerConnection);
          });
          session.on('accepted', () => attachPeerConnectionAudio(session.connection));
          session.on('confirmed', () => attachPeerConnectionAudio(session.connection));
          attachPeerConnectionAudio(session.connection);

          if (direction === 'inbound') setState('ringing-in');
          else setState('ringing-out');

          setCall({ direction, peer, startedAt: Date.now(), session, remoteStream: null, localStream: null });
        });

        uaRef.current = ua;
        ua.start();
        registrationTimeoutRef.current = window.setTimeout(() => {
          if (cancelled || uaRef.current !== ua || ua.isRegistered?.()) return;
          setState('register-failed');
          if (!wsConnectedRef.current) {
            setError(
              `Kan de PBX niet bereiken op ${cfg.ws_uri}. De WebSocket-poort is niet bereikbaar — controleer de firewall/router en het TLS-certificaat.`
            );
          } else {
            setError(
              'PBX bereikt, maar geen SIP REGISTER-response binnen 10s. Controleer of de extensie bestaat, het wachtwoord klopt en transport = WSS staat ingesteld.'
            );
          }
          try { ua.stop(); } catch { /* ignore */ }
        }, 10000);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'SIP-verbindingsfout');
          setState('register-failed');
        }
      }
    })();

    return () => {
      cancelled = true;
      clearRegistrationTimeout();
      try { uaRef.current?.stop(); } catch { /* ignore */ }
      uaRef.current = null;
    };
  }, [cfg, attachPeerConnectionAudio, activationKey]);

  const dial = useCallback(async (target: string) => {
    const ua = uaRef.current;
    if (!ua || !cfg) return;
    ensureRemoteAudioElement();
    unlockRemoteAudio();
    setError(null);
    const iceServers: RTCIceServer[] = [
      { urls: cfg.stun_server || 'stun:stun.l.google.com:19302' },
      {
        urls: 'turns:freepbx.isoft.nl:5349',
        username: 'isoftturn',
        credential: 'TurnIsoft2026Relay',
      },
    ];
    const host = cfg.sip_uri.split('@')[1] ?? '';
    const prefix = (cfg.dial_prefix ?? '').trim();
    const dialNumber = target.includes('@')
      ? target
      : `${prefix}${target}`;
    const targetUri = dialNumber.includes('@') ? dialNumber : `sip:${dialNumber}@${host}`;
    let mediaStream: MediaStream | undefined;
    try {
      mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    } catch (err) {
      setError(`Geen microfoontoegang: ${err instanceof Error ? err.message : String(err)}`);
      return;
    }
    setCall((c) => (c ? { ...c, localStream: mediaStream! } : c));
    ua.call(targetUri, {
      mediaStream,
      mediaConstraints: { audio: true, video: false },
      pcConfig: { iceServers },
      rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
    });
    // After call() the session exists; ensure localStream is attached
    setTimeout(() => {
      setCall((c) => (c && !c.localStream ? { ...c, localStream: mediaStream! } : c));
    }, 0);
  }, [cfg, ensureRemoteAudioElement, unlockRemoteAudio]);

  const hangup = useCallback(() => {
    try { call?.session?.terminate(); } catch { /* ignore */ }
    try { call?.localStream?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    setCall(null);
  }, [call]);

  const toggleMute = useCallback(() => {
    const s = call?.session;
    if (!s) return false;
    const wasMuted = !!s.isMuted().audio;
    const next = !wasMuted;
    if (next) s.mute({ audio: true }); else s.unmute({ audio: true });
    try {
      const pc: RTCPeerConnection | undefined = s.connection;
      pc?.getSenders().forEach((sender) => {
        if (sender.track && sender.track.kind === 'audio') sender.track.enabled = !next;
      });
    } catch { /* ignore */ }
    return next;
  }, [call]);

  const isConnected = state === 'in-call';
  const isInCall = state === 'in-call' || state === 'ringing-out' || state === 'ringing-in';

  return { state, call, error, dial, hangup, toggleMute, isConnected, isInCall };
}